import React, { useCallback, useEffect, useState } from "react";
import {
  AppraisalDocProcessingOutput,
  AppraisalQuestionnaire,
  DocumentFiles,
  PracticeAttachment,
  PracticeDocument,
  PracticeDocumentsByCategory,
  ReadOnlyPractice,
  UploadAdzApiResponse,
  useFinalisePracticeMutation,
  useLazyGetQuestionnaireQuery,
  useUploadAdzMutation,
  useUploadPracticeDocumentMutation,
} from "../../../../../../redux/apiSpecifications/apiCrud";
import { ADZPreviewStatuses } from "./ADZPreview";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { SerializedError } from "@reduxjs/toolkit";
import { fileToBase64 } from "../../../../../../utils/fileUtils";
import { removeMimeType } from "../../../../../../utils/base64";
import { PrimaId } from "../../../../../../config/const";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../../redux/store";
import CongruityReport from "./CongruityReport";
import { Alert, Space } from "antd";
import { useTranslation } from "react-i18next";
import PostConcludePracticePresentational from "./PostConcludePracticePresentational";
import { useNotifications } from "../../../../../../hooks/useNotifications";

// Types

export type PostConcludePracticeStatuses =
  | "select-is-negative"
  | "load-adz"
  | "loading-adz"
  | "loading-document"
  | "adz-valid"
  | "adz-invalid"
  | "adz-valid-with-warnigns"
  | "adz-without-agreement"
  | "questionnare"
  | "load-proof-of-access-error"
  | "solve-missing-agreement"
  | "saving-questionnaire"
  | "saving-questionnaire-error"
  | "questionnaire-saved"
  | "finalizing-practice"
  | "practice-finalized"
  | "finalizing-practice-error"
  | "retrieve-practice-status"
  | "practice-status-refreshed"
  | "loading-questionnaire";

/**
 * @desccription
 * @interface IConcludePracticeProps
 * @property {ReadOnlyPractice} ipractice - object practice
 */

export type UpdateQuestionnaireDataType = {
  questionnaire: {
    agreement: string;
    isVocalAgreement?: boolean;
  };
  documents?: PracticeDocument[];
  status: PostConcludePracticeStatuses;
};

const removeMimetypeFromQuestionnarieAttachments = (
  questionnaire: AppraisalQuestionnaire
) => {
  const updatedQuestionnaire: AppraisalQuestionnaire = {
    ...questionnaire,
    documents: (questionnaire.documents || []).map((d) => ({
      ...d,
      attachmentsFiles: (d.attachmentsFiles || []).map((a) => ({
        ...a,
        base64File:
          a.base64File !== null ? removeMimeType(a.base64File!) : undefined,
      })),
    })),
  };

  return updatedQuestionnaire;
};

interface IPostConcludedPracticeProps {
  practice: ReadOnlyPractice | undefined;
  onRefreshPractice: (practice: ReadOnlyPractice) => void;
}

/**
 * ConcludePractice component
 *
 * @component
 * @example
 * <ConcludePractice />
 */
const PostConcludedPractice: React.FC<IPostConcludedPracticeProps> = (
  props
) => {
  const { t } = useTranslation();

  // Common variables
  //const { getAuthorization } = useAuthorization();
  const { activeRole, authorization } = useSelector(
    (state: RootState) => state.user
  );

  const { displaySuccess, displayFetchBaseQueryErrorNotification } =
    useNotifications();

  // Local state
  const [practice, setPractice] = useState<ReadOnlyPractice | undefined>(
    props.practice
  );
  const [status, setStatus] =
    useState<PostConcludePracticeStatuses>("select-is-negative");

  const [adzDocument, setAdzDocument] = useState<
    PracticeAttachment | undefined
  >();

  const [adzResult, setAdzResult] =
    React.useState<AppraisalDocProcessingOutput>();
  const [adzStatus, setAdzStatus] = React.useState<ADZPreviewStatuses>("empty");
  const [adzErrorMessage, setADZErrorMessage] = React.useState<string>();

  const [questionnaireResult, setQuestionnaireResult] =
    useState<AppraisalQuestionnaire>({} as AppraisalQuestionnaire);

  const [refreshedPracticeStatus, setRefreshedPracticeStatus] =
    useState<string>(practice?.status || "");

  const [isFinalized, setIsFinalized] = useState<boolean>(false);

  // TEMP
  const [desiredOutput, setDesiredOutput] = React.useState<number>(1);
  const [desiredClient, setDesiredClient] = React.useState<number>(1);
  const [desiredSaveQuestionnaireResult, setDesiredSaveQuestionnaireResult] =
    React.useState<number>(1);
  const [desiderdUpdatedStatus, setDesiderdUpdatedStatus] =
    React.useState<string>("CO");

  const isLocalhost = window.location.href.indexOf("localhost") > -1;

  const isPrima = (() => {
    if (isLocalhost) return desiredClient === 1; // temp x local testing

    return practice?.idTenant === PrimaId;
  })();

  // API
  const [uploadADZ] = useUploadAdzMutation();
  const [uploadDocument] = useUploadPracticeDocumentMutation();
  const [finalizePractice] = useFinalisePracticeMutation();
  const [loadQuestionnaire] = useLazyGetQuestionnaireQuery();

  // Use effects
  useEffect(() => {
    setPractice(props.practice);
  }, [props.practice]);

  /**
   * Check the result of the ADZ
   */
  useEffect(() => {
    if (!adzResult) return;

    const errors =
      adzResult?.alerts?.filter((a) => a.alertType === "error") || [];
    const warnings =
      adzResult?.alerts?.filter((a) => a.alertType === "warning") || [];

    let updatedADZStatus: ADZPreviewStatuses = "empty";
    let updatedStatus: PostConcludePracticeStatuses = "load-adz";

    if (adzErrorMessage) {
      updatedStatus = "adz-invalid";
      updatedADZStatus = "invalid";
    } else {
      if (errors?.length === 0) {
        if (warnings?.length === 0) {
          // no errors & no warnings
          updatedStatus = "questionnare";
          updatedADZStatus = "valid";
        } else {
          // no errors & warnings
          // WCC001 - "Accordo non Presente"
          if (warnings?.find(({ alertCode }) => alertCode === "WCC001")) {
            updatedStatus = "adz-without-agreement";
            updatedADZStatus = "invalid";
          } else {
            updatedStatus = "adz-valid-with-warnigns";
            updatedADZStatus = "valid";
          }
        }
      } else {
        // errors
        updatedStatus = "adz-invalid";
        updatedADZStatus = "invalid";
      }
    }

    setAdzStatus(updatedADZStatus);
    setStatus(updatedStatus);
  }, [adzResult, adzErrorMessage]);

  // if it's concluded or in review then load questionnaire
  useEffect(() => {
    if (
      practice?.id === undefined ||
      ["CO", "IV", "CI", "CR", "CF"].includes(practice?.status || "") == false
    )
      return;
    (async () => {
      setStatus("loading-questionnaire");

      const { data, isSuccess } = await loadQuestionnaire({
        authorization,
        id: practice?.id!,
      });
      if (isSuccess) {
        setQuestionnaireResult(data as AppraisalQuestionnaire);
        setAdzStatus("valid");
      } else {
        // to do
      }

      if (["CO", "CI", "CR", "CF"].includes(practice?.status || "")) {
        setStatus("practice-finalized");
      } else if (practice?.status === "IV") {
        setStatus("questionnaire-saved");
      }
    })();
  }, [practice?.id, practice?.status]);

  // HANDLES
  /**
   * Handles the loading of an ADZ file
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e - The file select event object
   * @returns {Promise<void>}
   */

  const handleResetLoadADZ = () => {
    setRefreshedPracticeStatus("CP");
    setStatus("select-is-negative");
  };

  const handleLoadADZ = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!e || !e.target || !e.target.files) return;

      const selectedFile = e.target.files[0];
      if (!selectedFile) return;

      let base64File = await fileToBase64(selectedFile);
      base64File = removeMimeType(base64File);

      setStatus("loading-adz");
      setAdzStatus("empty");
      setADZErrorMessage(undefined);

      const response = await uploadADZ({
        authorization,
        id: isLocalhost ? desiredOutput : practice?.id!,
        adzUpload: {
          id: 0,
          version: 2,
          originalName: selectedFile.name,
          base64File,
          isNegative: questionnaireResult.isNegative,
        },
      });

      const correctResponse = response as { data: UploadAdzApiResponse };
      const errorResponse = response as {
        error: FetchBaseQueryError | SerializedError;
      };

      if (correctResponse.data) {
        const output = correctResponse.data as AppraisalDocProcessingOutput;
        const message = correctResponse.data as string;

        setAdzDocument({
          id: 0,
          originalName: selectedFile.name,
          base64File,
          name: "ADZ",
        });

        setQuestionnaireResult({
          isNegative: questionnaireResult.isNegative,
          negativeReasonCode: questionnaireResult.negativeReasonCode,
          accessProof: output.isAccessProofFile,
          agreement: output.agreement,
          isVerbalAgreement: output.isVerbalAgreement,
          isForcedAgreement: output.isForcedAgreement,
          isConsistent: "",
          isUneconomic: output.isUneconomic,
          notes: "",
          documents: [],
        });
        if (message && message.length > 0) {
          // it's like an error
          setADZErrorMessage(message);
          setAdzResult(undefined);
        } else {
          setAdzResult(output);
        }
      } else if (errorResponse.error) {
        setStatus("adz-invalid");
      }
    },
    [
      desiredOutput,
      questionnaireResult.isNegative,
      questionnaireResult.negativeReasonCode,
    ]
  );

  const handleAddProofOfAccess = useCallback(
    async (practiceDocument: PracticeDocument) => {
      const prevStatus = status;
      setStatus("loading-document");
      const response = await uploadDocument({
        authorization,
        id: practice?.id!,
        practiceDocument,
      });
      const correctResponse = response as {
        data: PracticeDocumentsByCategory;
      };
      const errorResponse = response as {
        error: FetchBaseQueryError | SerializedError;
      };

      if (correctResponse.data) {
        // not need to update practice documents
        setStatus(prevStatus);
      } else if (errorResponse.error) {
        setStatus("load-proof-of-access-error");
      }
    },
    [status]
  );

  const handleRestart = () => setStatus("load-adz");

  const handleStartQuestionnaire = () => setStatus("questionnare");

  const handleResolveMissingAgreement = () =>
    setStatus("solve-missing-agreement");

  const handleUpdateLocalQuestionnaire = ({
    questionnaire,
    documents,
    status: newStatus,
  }: UpdateQuestionnaireDataType) => {
    setStatus(newStatus);

    const updatedADZStatus = newStatus === "questionnare" ? "valid" : "empty";
    setAdzStatus(updatedADZStatus);

    const updatedQuestionnaireResult = {
      ...questionnaireResult,
      agreement: questionnaire.agreement,
      isVerbalAgreement: questionnaire.isVocalAgreement,
      documents: [
        ...(questionnaireResult.documents || []),
        ...(documents || []),
      ],
    };

    setQuestionnaireResult(updatedQuestionnaireResult);
  };

  // const handleNoConfirmAgreement = () => {
  //   setStatus("load-adz");
  //   setAdzStatus("empty");
  // };

  const handleChangeIsNegative = (value: boolean | undefined) => {
    const updatedQuestionnaireResult = {
      ...questionnaireResult,
      isNegative: value,
      negativeReasonCode: "",
    };
    setQuestionnaireResult(updatedQuestionnaireResult);
  };

  const handleChangeNegativeReason = (value: string | undefined) => {
    const updatedQuestionnaireResult = {
      ...questionnaireResult,
      negativeReasonCode: value,
    };
    setQuestionnaireResult(updatedQuestionnaireResult);
  };

  const handleReset = () => {
    debugger;
    setStatus("select-is-negative");
    setAdzStatus("empty");
    setADZErrorMessage(undefined);
    setAdzResult(undefined);
    handleChangeIsNegative(undefined);
    handleChangeNegativeReason("");
  };

  const handleConcludePracticeST = async () => {
    setStatus("finalizing-practice");

    const response = await finalizePractice({
      authorization,
      id: isLocalhost ? desiredSaveQuestionnaireResult : practice?.id!, // temp x local testing
      activeRole: activeRole!,
      appraisalQuestionnaire: {},
    });

    const errorResponse = response as {
      error: FetchBaseQueryError | SerializedError;
    };

    if (isLocalhost) {
      if (desiredSaveQuestionnaireResult === 1) {
        setStatus("practice-finalized");
        setIsFinalized(true);
      } else {
        setStatus("finalizing-practice-error");
      }
    } else {
      // this time I have to check the error, because the correct response is empty
      if (errorResponse.error) {
        setStatus("finalizing-practice-error");
      } else {
        setStatus("practice-finalized");
      }
    }
  };

  const handleConcludePractice = async (
    appraisalQuestionnaire: AppraisalQuestionnaire | null
  ) => {
    if (!appraisalQuestionnaire) {
      handleConcludePracticeST();
      return;
    }

    setStatus("finalizing-practice");
    const updatedQuestionnaire: AppraisalQuestionnaire = {
      ...questionnaireResult,

      isNegative: appraisalQuestionnaire.isNegative,
      negativeReasonCode: appraisalQuestionnaire.negativeReasonCode,
      accessProof: appraisalQuestionnaire.accessProof,
      agreement: appraisalQuestionnaire.agreement,
      isConsistent: appraisalQuestionnaire.isConsistent,
      isUneconomic: appraisalQuestionnaire.isUneconomic,
      notes: appraisalQuestionnaire.notes,
    };

    setQuestionnaireResult(updatedQuestionnaire);

    const response = await finalizePractice({
      authorization,
      id: isLocalhost ? desiredSaveQuestionnaireResult : practice?.id!, // temp x local testing
      activeRole: activeRole!,
      version: 2,
      appraisalQuestionnaire:
        practice?.practiceTypeCode === "7"
          ? ({ documents: [] } as AppraisalQuestionnaire)
          : removeMimetypeFromQuestionnarieAttachments(updatedQuestionnaire),
    });

    const errorResponse = response as {
      error: FetchBaseQueryError | SerializedError;
    };

    if (isLocalhost) {
      if (desiredSaveQuestionnaireResult === 1) {
        setStatus("practice-finalized");
        setIsFinalized(true);
      } else {
        setStatus("finalizing-practice-error");
      }
    } else {
      // this time I have to check the error, because the correct response is empty
      if (errorResponse.error) {
        setStatus("finalizing-practice-error");
        displayFetchBaseQueryErrorNotification(errorResponse.error);
      } else {
        setStatus("practice-finalized");
        displaySuccess(
          t("appraisals-conclude-practice-revision-status-message")
        );
      }
    }
  };

  const handleAddConservativeAgreement = (doc: DocumentFiles) => {
    const updatedQuestionnaireResult: AppraisalQuestionnaire = {
      ...questionnaireResult,
      agreement: "S",
      documents: [...(questionnaireResult.documents || []), doc],
    };

    setQuestionnaireResult(updatedQuestionnaireResult);
  };

  const handleRemoveConservativeAgreement = () => {
    const updatedQuestionnaireResult: AppraisalQuestionnaire = {
      ...questionnaireResult,
      agreement: "SN",
      documents: (questionnaireResult.documents || []).filter(
        (d) => d.documentTypeCode !== "ACC"
      ),
    };

    setQuestionnaireResult(updatedQuestionnaireResult);
  };

  const handleModifyQuestionnaire = () => {
    setStatus("questionnare");
  };

  const showwCongruityReport =
    ["CP", "CO", "CI", "CR", "CF"].includes(practice?.status || "") &&
    practice?.practiceTypeCode === "7";

  return showwCongruityReport ? (
    <CongruityReport
      idPractice={practice.id}
      concludePractice={handleConcludePractice}
      status={status}
      isRevisionPracticeTab={true}
    />
  ) : (
    <>
      <PostConcludePracticePresentational
        status={status}
        practiceStatus={practice?.status || ""}
        questionnaireResult={questionnaireResult}
        adzDocument={adzDocument}
        adzStatus={adzStatus}
        adzResult={adzResult}
        adzErrorMessage={adzErrorMessage}
        idPractice={practice?.id}
        isPrima={isPrima}
        isFinalized={isFinalized}
        practiceRefreshedStatus={refreshedPracticeStatus}
        onLoadADZ={handleLoadADZ}
        onRestart={handleRestart}
        onStartQuestionnaire={handleStartQuestionnaire}
        onResolveMissingAgreement={handleResolveMissingAgreement}
        onUpdateQuestionnaire={handleUpdateLocalQuestionnaire}
        onAddProofOfAccess={handleAddProofOfAccess}
        onTransmitToClient={handleConcludePractice}
        onChangeIsNegative={handleChangeIsNegative}
        onChangeIsNegativeReason={handleChangeNegativeReason}
        onReset={handleReset}
        onAddConservativeAgreement={handleAddConservativeAgreement}
        onRemoveConservativeAgreement={handleRemoveConservativeAgreement}
        onModifyQuestionnaire={handleModifyQuestionnaire}
        onResetLoadADZ={handleResetLoadADZ}
      />

      {practice?.status === "RE" && (
        <Space direction="vertical" style={{ width: "100%" }}>
          <Alert
            message={t("rejected-practice-info-message", {
              rejectionReason: t(
                `rejection-reason-description-${practice?.rejectionReasonCode}`
              ),
              rejectionNote: practice?.rejectionReasonNote,
            })}
            type="info"
          />
        </Space>
      )}

      {practice?.status === "RE" && practice?.isViewed && (
        <Space direction="vertical" style={{ width: "100%" }}>
          <Alert
            message={t("rejected-practice-appraiser-acknowledgment", {
              viewDate: practice?.isViewedDate,
            })}
            type="info"
          />
        </Space>
      )}

      {isLocalhost && (
        <div>
          <select
            value={desiredOutput}
            onChange={(e) => setDesiredOutput(parseInt(e.target.value))}
          >
            <option value={1}>OK</option>
            <option value={2}>errors</option>
            <option value={3}>blocking warnigs</option>
            <option value={4}>easy warning</option>
            <option value={5}>errors & warnings</option>
            <option value={6}>message</option>
            <option value={7}>no button</option>
          </select>

          <select
            value={desiredClient}
            onChange={(e) => setDesiredClient(parseInt(e.target.value))}
          >
            <option value={1}>Prima</option>
            <option value={2}>Altro</option>
          </select>
          <select
            value={desiredSaveQuestionnaireResult}
            onChange={(e) =>
              setDesiredSaveQuestionnaireResult(parseInt(e.target.value))
            }
          >
            <option value={1}>Ok</option>
            <option value={2}>error</option>
          </select>

          <select
            value={desiderdUpdatedStatus}
            onChange={(e) => setDesiderdUpdatedStatus(e.target.value)}
          >
            <option value={"CO"}>conclusa</option>
            <option value={"AC"}>timer</option>
          </select>
          <button onClick={handleReset}>reset</button>
        </div>
      )}
    </>
  );
};

export default PostConcludedPractice;
